import flatMap from 'lodash.flatmap';
import { pruneObject } from 'cio/utils/objects';
import { AndFilter, FilterCondition, Filters, OrFilter } from 'cio/utils/filters';
import { SerializedLegacyAttributeFilter, SerializedLegacyFilter } from 'cio/types/conditions/legacy-filter';
import conditionToNamedBitmap from './named-bitmap';
import {
  isAttributeCompareCondition,
  isAttributeCondition,
  isCollectionAttributeCondition,
  isDeviceAttributeCondition,
  isEventAttributeCondition,
  isForeignEventAttributeCondition,
  isMessageCondition,
  isObjectAttributeCondition,
  isRelationshipAttributeCondition,
  isSerializedNewsletterRecipientCondition,
  isSerializedSegmentNegatableCondition,
} from 'cio/utils/conditions/deserialize';
import { typeOf } from '@ember/utils';
import { ConditionType } from 'cio/utils/condition-types';

function unpackForeignEventsAndMessages(conditions: any[]) {
  return flatMap(conditions, (condition) => {
    if (condition.type === 'message' && condition.isForeignEvent === true) {
      // when saving filters need to be unpacked into the foreign_event_attribute
      let filtersToUnpack: SerializedLegacyAttributeFilter[] = [];
      let { filters } = condition.event;

      if (typeOf(filters) === 'object' && Object.keys(filters).length > 0) {
        filtersToUnpack = [{ ...filters, type: 'foreign_event_attribute' }];
      }

      return [
        {
          event_name: condition.event.name,
          event_type: condition.event.type,
          type: 'foreign_event',
        },
        ...filtersToUnpack,
      ];
    }

    if (condition.type === 'foreign_event' && (condition as any).event != null) {
      let { filters } = (condition as any).event;
      let filtersToUnpack: any[] = [];

      // @ts-ignore Ember's typeOf isn't typed correctly
      if (typeOf(filters) === 'object') {
        if (filters.and) {
          filtersToUnpack = [...filters.and];
        } else if (filters.or) {
          throw new Error('OR filters are not supported for foreign events');
        } else if (Object.keys(filters).length > 0) {
          filtersToUnpack = [filters];
        }
      }

      filtersToUnpack = filtersToUnpack.map((filter: any) => {
        if (filter.type === 'attribute_compare') {
          return { ...filter, field_source: 'foreign_event_attribute' };
        } else if (filter.type == null) {
          return {
            type: 'foreign_event_attribute',
            ...filter,
          };
        }

        return filter;
      });

      return [
        {
          type: 'foreign_event',
          event_name: (condition as any).event.name,
        },
        ...filtersToUnpack,
      ];
    }

    return condition;
  });
}

export default function serializeFilters(
  conditions: SerializedLegacyFilter[],
  operator: FilterCondition,
  useFilters: true,
  mapAttributes?: boolean,
): Filters;
export default function serializeFilters(
  conditions: SerializedLegacyFilter[],
  operator: FilterCondition,
  useFilters?: boolean,
  mapAttributes?: boolean,
): SerializedLegacyFilter[];
export default function serializeFilters(
  conditions: SerializedLegacyFilter[],
  operator: FilterCondition,
  useFilters = false,
  mapAttributes = false,
): SerializedLegacyFilter[] | Filters {
  if (!useFilters) {
    return unpackForeignEventsAndMessages(conditions);
  }

  if (!conditions.length) {
    return { [operator]: [] } as unknown as AndFilter | OrFilter;
  }

  let filters = flatMap(conditions, (condition: SerializedLegacyFilter) => {
    if (
      isAttributeCondition(condition) ||
      isEventAttributeCondition(condition) ||
      isForeignEventAttributeCondition(condition)
    ) {
      return createAttributeBasedFilter(ConditionType.Attribute, condition, mapAttributes);
    } else if (isObjectAttributeCondition(condition)) {
      return createAttributeBasedFilter(ConditionType.ObjectAttribute, condition, mapAttributes);
    } else if (isRelationshipAttributeCondition(condition)) {
      return createAttributeBasedFilter(ConditionType.RelationshipAttribute, condition, mapAttributes);
    } else if (isAttributeCompareCondition(condition)) {
      let value = {
        operator: condition.operator.replace('!', ''),
        field_source: condition.field_source,
        field: condition.field,
        value_source: condition.value_source,
        value: condition.value,
      };
      let attributeCompare = {};
      if (condition.field_source === ConditionType.ForeignEventAttribute) {
        attributeCompare = {
          foreign_event: {
            attribute_compare: { ...value, field_source: ConditionType.ForeignEventAttribute },
          },
        };
      } else {
        attributeCompare = {
          map: {
            attribute_compare: value,
          },
        };
      }

      return wrapInNotFilterIf(attributeCompare, condition.operator.startsWith('!'));
    } else if (isCollectionAttributeCondition(condition)) {
      return createAttributeBasedFilter('collection_attribute', condition, mapAttributes);
    } else if (isDeviceAttributeCondition(condition)) {
      return createAttributeBasedFilter('device_attribute', condition, mapAttributes);
    } else if (isSerializedSegmentNegatableCondition(condition)) {
      let filters = {
        or: condition.segments.map((id) => ({
          segment: { id },
        })),
      };
      return wrapInNotFilterIf(filters, condition.negate);
    } else if (isMessageCondition(condition)) {
      return conditionToNamedBitmap(condition);
    } else if (isSerializedNewsletterRecipientCondition(condition)) {
      let newsletter = {
        newsletter_recipients: {
          id: condition.newsletter_id,
        },
      };
      return wrapInNotFilterIf(newsletter, condition.negate);
    }

    // This code is unreachable but TS won't shut up
    return undefined as any;
  });

  return { [operator]: filters } as unknown as AndFilter | OrFilter;
}

function createAttributeBasedFilter(name: string, condition: SerializedLegacyAttributeFilter, wrapInMap: boolean) {
  let inverse = condition.operator.startsWith('!');
  let filter = { [name]: pruneCondition(condition) };

  if ([ConditionType.ObjectAttribute, ConditionType.RelationshipAttribute].includes(name as ConditionType)) {
    filter[name].type_id = condition.type_id;
  }

  if (wrapInMap) {
    filter = {
      map: filter,
    };
  }

  if (condition.type === 'foreign_event_attribute') {
    return createForeignEventBasedFilter(condition);
  }

  return wrapInNotFilterIf(filter, inverse);
}

function createForeignEventBasedFilter(condition: SerializedLegacyAttributeFilter) {
  let inverse = condition.operator.startsWith('!');
  let filter = {
    foreign_event: {
      attribute: pruneCondition(condition),
    },
  };
  return wrapInNotFilterIf(filter, inverse);
}

function pruneCondition(condition: SerializedLegacyAttributeFilter) {
  return pruneObject({
    field: condition.field,
    value: condition.value,
    value2: condition.value2,
    operator: condition.operator.replace('!', ''),
  });
}

function wrapInNotFilterIf(filter: any, shouldWrap: boolean) {
  if (shouldWrap) {
    return {
      not: filter,
    };
  }
  return filter;
}
